import { useApolloClient, useMutation } from "@apollo/client";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import { Box, Button, Link, Paper, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DiscrepancyBubblesCell } from "components/DiscrepancyBubblesCell";
import { ExportDataButton } from "components/ExportDataButton";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  StaffManifestReviewManifestsQuery,
  StaffManifestReviewManifestsQueryVariables,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useRefetchOnMutationClose } from "hooks/useRefetchOnMutationClose";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DiscrepancyReviewDialog } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog";
import { MANIFEST_BLOCK_REASON_LABELS } from "routes/Customer/Waste/Manifests/manifestBlockReasonDisplay";
import { ManifestCommentsPopover } from "routes/Customer/Waste/Manifests/ManifestCommentsPopover";
import { ManifestDetails } from "routes/Customer/Waste/Manifests/ManifestDetails";
import { ManifestReviewDialog } from "routes/Customer/Waste/Manifests/ManifestReviewDialog";
import { SOURCE_LABEL } from "routes/Customer/Waste/Manifests/manifestSourceDisplay";
import { DocumentReviewPermalink } from "routes/Customer/Waste/Manifests/ReviewPermalink";
import { SourcesCell } from "routes/Customer/Waste/Manifests/SourcesCell";
import { relativeLogTime } from "util/dates";

import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";
import { AssignDialog } from "../AssignDialog";
import {
  ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
  STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY,
  assigneeLabel,
  type ManifestQuickLink,
  type ManifestReviewRow,
} from "./api";
import { useManifestReviewDeepLink } from "./manifestDeepLink";
import { ManifestDetailDialog } from "./ManifestDetailDialog";
import {
  DocumentReviewCell,
  ManifestBlockedCell,
  ManifestReviewEManifestCell,
  StaffActionsRequiredCell,
} from "./ManifestReviewCells";
import { ManifestReviewSummary } from "./ManifestReviewSummary";
import { TenantFeatureFlagsProvider } from "./TenantFeatureFlagsProvider";

/** Two-line column header (the renderHeader ignores its params). */
const twoLineHeader = (line1: string, line2: string) =>
  function TwoLineHeader() {
    return (
      <Box
        sx={{
          lineHeight: 1.2,
          whiteSpace: "normal",
          fontWeight: 500,
          fontSize: "0.8125rem",
        }}
      >
        {line1}
        <br />
        {line2}
      </Box>
    );
  };

// "Since delegated" should always read as elapsed time ("3 weeks ago"), never flip to a
// bare date. relativeLogTime falls back to an absolute date past its cutoff, so set the
// cutoff effectively to never (100 years) to keep the whole column relative.
const ALWAYS_RELATIVE_CUTOFF_HOURS = 24 * 365 * 100;

// Structured filters shown in the omnisearch popover. Keys match what the resolver
// parses (tenant / status / assignee). All column-derived text filters are excluded so
// the popover shows only these dropdown/type-ahead controls; free-text matching of
// manifest #, tenant, facility, and EPA id is handled by the omnisearch box itself.
// Outstanding-action filter values (must match the resolver's staffAction keys) → labels.
const STAFF_ACTION_LABELS: Record<string, string> = {
  docReview: "Document Review",
  discrepancyReview: "Discrepancy Review",
  vendorContact: "Vendor Contact",
  exceptionReport: "Exception Report",
  epaCorrections: "EPA Corrections",
};

// Data-source facets for the multi-select Source filter (values match the resolver).
const SOURCE_LABELS: Record<string, string> = {
  eManifest: "e-Manifest",
  vendor: "Vendor",
  document: SOURCE_LABEL[WasteManifestSource.ManifestDocument],
};

// Document review states for the multi-select Document Review filter.
const DOC_REVIEW_STATE_LABELS: Record<string, string> = {
  NEEDS_REVIEW: "Needs Review",
  NEEDS_REFRESH: "New Version",
  REVIEWED: "Reviewed",
  NOT_PROCESSED: "Not Processed",
};

// Discrepancy lifecycle states for the Field / EPA status dropdowns ("Any" = no value).
const DISCREPANCY_STATUS_OPTIONS = ["UNRESOLVED", "PENDING", "RESOLVED"];
const DISCREPANCY_STATUS_LABELS: Record<string, string> = {
  UNRESOLVED: "Unresolved",
  PENDING: "Pending",
  RESOLVED: "Resolved",
};

// e-Manifest compliance states (values match the resolver / EMANIFEST_COMPLIANCE_FILTERS).
const EMANIFEST_COMPLIANCE_LABELS: Record<string, string> = {
  tsdfContactRequired: "TSDF Contact Required",
  tsdfContacted: "TSDF Contacted",
  exceptionRequired: "Exception Required",
  exceptionFiled: "Exception Filed",
};

// Reviewer-facing block reasons, widened to string keys for the filter's presentation fn.
const BLOCKED_REASON_LABELS: Record<string, string> = MANIFEST_BLOCK_REASON_LABELS;

// NB: a filter `key` that exactly matches a grid column `field` (e.g. "documentReview",
// "eManifestCompliance") loses its enumValues to the column definition, leaving an empty
// dropdown — so those filters use distinct keys ("documentReviewState", "compliance").
// Identity filters shared verbatim by the Open and Completed tabs.
const MANIFEST_NUMBER_FILTER: FilterKey = {
  key: "manifestNumber",
  header: "Manifest #",
  filterKeyType: "text",
};
const TENANT_FILTER: FilterKey = {
  key: "tenant",
  header: "Tenant",
  filterKeyType: "multipleTenant",
};
const FACILITY_FILTER: FilterKey = { key: "facility", header: "Facility", filterKeyType: "text" };
// Multi-select; the resolver treats multiple sources as AND (the manifest must have ALL
// chosen sources on file), mirroring the E/V/D pills.
const SOURCE_FILTER: FilterKey = {
  key: "source",
  header: "Sources",
  filterKeyType: "enumAutocomplete",
  enumValues: Object.keys(SOURCE_LABELS),
  enumPresentationFunction: (value) => SOURCE_LABELS[value] ?? value,
};

const MANIFEST_REVIEW_FILTERS: FilterKey[] = [
  MANIFEST_NUMBER_FILTER,
  TENANT_FILTER,
  FACILITY_FILTER,
  {
    key: "staffAction",
    header: "Staff actions required",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(STAFF_ACTION_LABELS),
    enumPresentationFunction: (value) => STAFF_ACTION_LABELS[value] ?? value,
  },
  SOURCE_FILTER,
  {
    key: "documentReviewState",
    header: "Document Review",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(DOC_REVIEW_STATE_LABELS),
    enumPresentationFunction: (value) => DOC_REVIEW_STATE_LABELS[value] ?? value,
  },
  {
    key: "fieldDiscrepancy",
    header: "Field discrepancies",
    filterKeyType: "enum",
    enumValues: DISCREPANCY_STATUS_OPTIONS,
    enumPresentationFunction: (value) => DISCREPANCY_STATUS_LABELS[value] ?? value,
  },
  {
    key: "epaCorrection",
    header: "EPA requested corrections",
    filterKeyType: "enum",
    enumValues: DISCREPANCY_STATUS_OPTIONS,
    enumPresentationFunction: (value) => DISCREPANCY_STATUS_LABELS[value] ?? value,
  },
  {
    key: "missingEManifest",
    header: "Missing e-Manifest",
    filterKeyType: "boolean",
  },
  {
    key: "compliance",
    header: "e-Manifest compliance",
    filterKeyType: "enum",
    enumValues: Object.keys(EMANIFEST_COMPLIANCE_LABELS),
    enumPresentationFunction: (value) => EMANIFEST_COMPLIANCE_LABELS[value] ?? value,
  },
  {
    key: "blockedReason",
    header: "Blocked reason",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(BLOCKED_REASON_LABELS),
    enumPresentationFunction: (value) => BLOCKED_REASON_LABELS[value] ?? value,
  },
  { key: "shippedDate", header: "Shipped date", filterKeyType: "date" },
  { key: "inTransit", header: "In transit (days)", filterKeyType: "number" },
  {
    key: "assignee",
    header: "Assignee",
    filterKeyType: "multipleStaffAnyUser",
  },
  // Duration filter: days since the manifest's latest open assignment (>/</= days).
  { key: "assignedAt", header: "Assigned (days ago)", filterKeyType: "number" },
];

// Column fields whose auto-generated free-text filters we suppress (replaced by the
// structured filters above, or covered by free-text search).
const MANIFEST_REVIEW_EXCLUDED_FILTERS = [
  "manifestTrackingNumber",
  "tenantName",
  "facilityName",
  "shippedDate",
  "sources",
  "documentReview",
  "encampDiscrepancies",
  "epaCorrections",
  "eManifestCompliance",
  "staffActionRequired",
  "blocked",
  "status",
  "type",
  "assignee",
  "assignedAt",
  "assignedBy",
];

// The tab lives in a dedicated URL param (shareable), separate from the omnisearch term
// so the two grids can share the same filter string.
const COMPLETION_STATE_PARAM = "completionState";

// Completed tab: only the identity filters carry over (tenant / facility / manifest #/
// source), plus a completion-date range and the assignee filter re-mapped to the
// completer identity. The open-work filters (staff actions, doc-review state, discrepancy
// status, e-Manifest compliance, in-transit, assigned-days-ago) are meaningless once a
// manifest is done, so they're omitted here.
const MANIFEST_REVIEW_COMPLETED_FILTERS: FilterKey[] = [
  MANIFEST_NUMBER_FILTER,
  TENANT_FILTER,
  FACILITY_FILTER,
  SOURCE_FILTER,
  { key: "completedDate", header: "Completed date", filterKeyType: "date" },
  // Same staff-user picker as the Open tab, but the resolver matches it against the
  // manifest's completer identity (who resolved / reviewed), not the open assignee.
  { key: "assignee", header: "Completed by", filterKeyType: "multipleStaffAnyUser" },
];

// Column fields whose auto-generated free-text filters we suppress on the Completed tab.
const MANIFEST_REVIEW_COMPLETED_EXCLUDED_FILTERS = [
  "manifestTrackingNumber",
  "tenantName",
  "facilityName",
  "shippedDate",
  "sources",
  "completedAt",
  "completedBy",
  "completedSummary",
  "assignee",
];

// Queue + scoreboard queries to refresh after any assignment write (or failed attempt).
const ASSIGNMENT_REFETCH_QUERIES = ["StaffManifestReviewManifests", "ManifestReviewSummary"];

// Columns shared verbatim by the Open and Completed grids. Manifest # depends on the
// per-page quick-link opener, so it's a factory; the rest are static.
function manifestNumberColumn(
  openQuickLink: (link: ManifestQuickLink) => void
): OmnisearchGridColDef<ManifestReviewRow> {
  return {
    field: "manifestTrackingNumber",
    headerName: "Manifest #",
    width: 170,
    renderCell: ({ row }) => (
      <Link
        component="button"
        type="button"
        underline="hover"
        color="inherit"
        onClick={(e) => {
          e.stopPropagation();
          openQuickLink({
            kind: "drawer",
            processedManifestId: row.processedManifestId,
            tenantId: row.tenantId,
          });
        }}
        sx={{ font: "inherit" }}
      >
        {row.manifestTrackingNumber}
      </Link>
    ),
  };
}

const TENANT_COLUMN: OmnisearchGridColDef<ManifestReviewRow> = {
  field: "tenantName",
  headerName: "Tenant",
  flex: 1,
  minWidth: 140,
};
const FACILITY_COLUMN: OmnisearchGridColDef<ManifestReviewRow> = {
  field: "facilityName",
  headerName: "Facility",
  flex: 1,
  minWidth: 140,
  valueGetter: (_value, row) => row.facilityName ?? row.facilityEpaId ?? "—",
};
// UTC medium format to match the customer Manifests grid.
const BLOCKED_COLUMN: OmnisearchGridColDef<ManifestReviewRow> = {
  field: "blocked",
  headerName: "Blocked by",
  // Wide enough for the longest reason label ("Comparison looks wrong") — the reason
  // set is a closed enum, so the text never truncates.
  width: 200,
  sortable: false,
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => <ManifestBlockedCell row={row} />,
};

const SHIPPED_DATE_COLUMN: OmnisearchGridColDef<ManifestReviewRow> = {
  field: "shippedDate",
  headerName: "Shipped Date",
  width: 130,
  valueFormatter: (value) => (value ? getUtcDisplayDate(value, "med") : "—"),
};

export function ManifestReviewPage() {
  useBreadcrumb({
    label: "Manifest Review",
    to: "/staff/fulfillment/waste/manifest-review",
  });
  const { success, error } = useAlerts();

  const [searchParams, setSearchParams] = useSearchParams();

  const writeOmnisearch = (next: Record<string, string[]>) => {
    const str = stringifyOmnisearch(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (str) {
          params.set("omnisearch", str);
        } else {
          params.delete("omnisearch");
        }
        return params;
      },
      { replace: true }
    );
  };

  // Upsert/remove several keyed terms at once (clickable summary boxes / reviewer rows);
  // a null value clears that key. Preserves free text + untouched terms.
  const applyOmnisearch = (updates: Record<string, string[] | null>) => {
    const next = { ...parseOmnisearch(searchParams.get("omnisearch") ?? "") };
    for (const [key, value] of Object.entries(updates)) {
      if (value && value.length > 0) {
        next[key] = value;
      } else {
        delete next[key];
      }
    }
    writeOmnisearch(next);
  };

  // Current keyed omnisearch terms — lets the summary tiles render their on/off state.
  const activeTerms = useMemo(
    () => parseOmnisearch(searchParams.get("omnisearch") ?? ""),
    [searchParams]
  );

  // Which tab is active (Open by default), driven by a shareable URL param. The Completed
  // tab shows finished manifests; the two share the same omnisearch filter string.
  const completionState =
    searchParams.get(COMPLETION_STATE_PARAM) === "COMPLETED" ? "COMPLETED" : "OPEN";
  const isCompleted = completionState === "COMPLETED";
  // The active grid's totalCount, lifted out so the tab label can show it (the pivoting
  // count that respects the current filter).
  const [gridCount, setGridCount] = useState<number | undefined>(undefined);
  const setCompletionState = (next: "OPEN" | "COMPLETED") => {
    // Clear the lifted count and any row selection on switch so the newly-selected tab
    // doesn't flash the previous tab's count, and an Open-tab selection can't leak into
    // its bulk actions after visiting Completed.
    setGridCount(undefined);
    setSelectedRows([]);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "COMPLETED") params.set(COMPLETION_STATE_PARAM, "COMPLETED");
        else params.delete(COMPLETION_STATE_PARAM);
        // The URL-driven grid shares the `page` param across both tabs, so drop it on
        // switch: a stale page-2 offset would otherwise land on an empty page of the
        // other tab's (usually smaller) result set.
        params.delete("page");
        return params;
      },
      { replace: true }
    );
  };

  // Toggle a single keyed value on/off (summary scoreboard tiles). Multi-value keys
  // (e.g. staffAction) add/remove just this value so several tiles combine; `exclusive`
  // keys (assignee, missingEManifest) set or clear the whole key.
  const toggleFilter = (key: string, value: string, opts?: { exclusive?: boolean }) => {
    const next = { ...parseOmnisearch(searchParams.get("omnisearch") ?? "") };
    const existing: string[] = next[key] ?? [];
    const isActive = existing.includes(value);
    if (opts?.exclusive) {
      if (isActive) delete next[key];
      else next[key] = [value];
    } else if (isActive) {
      const remaining = existing.filter((entry) => entry !== value);
      if (remaining.length > 0) next[key] = remaining;
      else delete next[key];
    } else {
      next[key] = [...existing, value];
    }
    writeOmnisearch(next);
  };

  const [selectedRows, setSelectedRows] = useState<ManifestReviewRow[]>([]);
  // Drive the grid's selection from page state (not just mirror it): the grid caches
  // selected row objects across refetches (cross-page selection), so without this a row
  // that no longer exists after a refetch would stay silently selected and poison the
  // next bulk assignment with its stale id.
  const selectedIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  // Rows targeted by the assign picker (a single row from ＋/the modal, or the selection).
  const [assignTargets, setAssignTargets] = useState<ManifestReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<ManifestReviewRow | null>(null);

  // Deep link (e.g. from the unified work queue): `?manifest=<processedManifestId>`
  // self-hydrates the row by id and opens its detail dialog, whatever the
  // grid's tab, filters, or page show. One-shot: the param drops once acted on.
  useManifestReviewDeepLink(setDetailRow);
  // In-place quick link (manifest drawer / document review / discrepancy dialog) opened
  // from a row's links + staff-action icons, instead of navigating to the customer page.
  const [quickLink, setQuickLink] = useState<ManifestQuickLink | null>(null);
  const openQuickLink = useCallback((link: ManifestQuickLink) => setQuickLink(link), []);

  const apolloClient = useApolloClient();

  // The discrepancy dialog's grid refetch: whole-queue SQL aggregates the
  // dialog's mutations can't patch in the normalized cache, too expensive to
  // run per row action — refresh once per review session on close, but only
  // if `onMutated` fired, so a read-only open-and-close of the dialog on 50
  // manifests just to look doesn't trigger 50 whole-queue aggregates.
  const { onMutated: onDiscrepancyDialogMutated, onClose: onDiscrepancyDialogClose } =
    useRefetchOnMutationClose(
      () => setQuickLink(null),
      () => apolloClient.refetchQueries({ include: ASSIGNMENT_REFETCH_QUERIES }),
      () => error("Failed to refresh the manifest review queue — please reload the page")
    );

  const [assignManifests, { loading: assigning }] = useMutation(
    ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
    { refetchQueries: ASSIGNMENT_REFETCH_QUERIES }
  );

  const applyAssignment = useCallback(
    async (rows: ManifestReviewRow[], staffUserId: string | null) => {
      try {
        const { data } = await assignManifests({
          variables: {
            processedManifestIds: rows.map((row) => row.processedManifestId),
            staffUserId,
          },
        });
        const result = data?.assignManifestReviewManifests;
        const itemCount =
          (result?.assignedDiscrepancyCount ?? 0) + (result?.assignedDocumentReviewCount ?? 0);
        if (itemCount === 0) {
          // Every item on these manifests was resolved/reviewed since the page loaded —
          // nothing left to assign; the refetch reconciles the rows.
          success("Nothing to assign — these manifests have no active review items");
          return;
        }
        const verb = staffUserId ? "Assigned" : "Unassigned";
        success(`${verb} ${rows.length} manifest${rows.length === 1 ? "" : "s"}`);
      } catch {
        // Most likely the queue changed under the page (e.g. a manifest no longer
        // exists) — refresh it so the next attempt operates on current rows. Await the
        // refetch: if it also fails the grid may be stale, so tell the user to reload
        // rather than leaving them with a "try again" toast over un-refreshed rows.
        try {
          await apolloClient.refetchQueries({
            include: ASSIGNMENT_REFETCH_QUERIES,
          });
          error("Failed to update assignment — refreshed the queue, please try again");
        } catch {
          error(
            "Failed to update assignment and couldn't refresh the queue — please reload the page"
          );
        }
      }
    },
    [assignManifests, apolloClient, success, error]
  );

  const openAssignFor = useCallback((rows: ManifestReviewRow[]) => {
    if (rows.length === 0) return;
    setAssignTargets(rows);
    setAssignDialogOpen(true);
  }, []);

  const handleAssign = async (staffUserId: string) => {
    await applyAssignment(assignTargets, staffUserId);
    setAssignDialogOpen(false);
    setAssignTargets([]);
    setSelectedRows([]);
  };

  const rawColumns: OmnisearchGridColDef<ManifestReviewRow>[] = useMemo(
    () => [
      manifestNumberColumn(openQuickLink),
      TENANT_COLUMN,
      FACILITY_COLUMN,
      {
        field: "sources",
        headerName: "Sources",
        width: 96,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => <SourcesCell availableSources={row.availableSources} />,
      },
      // Hidden by default (see initialColumnVisibilityModel).
      SHIPPED_DATE_COLUMN,
      {
        field: "staffActionRequired",
        headerName: "Staff Actions Required",
        // Fixed width that just fits the five circled icons (no flex grow).
        width: 168,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <StaffActionsRequiredCell row={row} onOpenQuickLink={openQuickLink} />
        ),
      },
      {
        field: "documentReview",
        headerName: "Document Review",
        renderHeader: twoLineHeader("Document", "Review"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <DocumentReviewCell
            state={row.documentReviewState}
            onClick={() =>
              openQuickLink({
                kind: "docReview",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                documentIds: row.documentIds,
              })
            }
          />
        ),
      },
      {
        field: "encampDiscrepancies",
        headerName: "Field Discrepancies",
        renderHeader: twoLineHeader("Field", "Discrepancies"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        // Bubble style; orange = unresolved, blue = pending, green = resolved (field-level only).
        renderCell: ({ row }) => (
          <DiscrepancyBubblesCell
            unresolved={row.encampUnresolved ?? 0}
            pending={row.encampPending ?? 0}
            resolved={row.encampResolved ?? 0}
            unresolvedColor="warning"
            pendingColor="info"
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "field",
                manifestTrackingNumber: row.manifestTrackingNumber,
              })
            }
          />
        ),
      },
      {
        field: "epaCorrections",
        headerName: "EPA Requested Corrections",
        renderHeader: twoLineHeader("EPA Requested", "Corrections"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        // Bubble style (same as Field); red = unresolved, blue = pending, green = resolved.
        // Counts are EPA_CORRECTION_REQUEST-kind only — missing-manifest discrepancies
        // are surfaced in the e-Manifest Compliance column, not counted here.
        renderCell: ({ row }) => (
          <DiscrepancyBubblesCell
            unresolved={row.epaUnresolved ?? 0}
            pending={row.epaPending ?? 0}
            resolved={row.epaResolved ?? 0}
            unresolvedColor="error"
            pendingColor="info"
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "epaCorrection",
                manifestTrackingNumber: row.manifestTrackingNumber,
              })
            }
          />
        ),
      },
      {
        field: "eManifestCompliance",
        headerName: "e-Manifest Compliance",
        renderHeader: twoLineHeader("e-Manifest", "Compliance"),
        width: 130,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <ManifestReviewEManifestCell
            row={row}
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "eManifestMissing",
                manifestTrackingNumber: row.manifestTrackingNumber,
              })
            }
          />
        ),
      },
      BLOCKED_COLUMN,
      {
        field: "assignee",
        headerName: "Assignee",
        flex: 1,
        minWidth: 140,
        // Server sort is keyed by column; assignee/assignedBy resolve to a name in JS and
        // aren't server-sortable, so don't offer a no-op sort affordance on them.
        sortable: false,
        // Empty assignee renders as "—" (a tooltip clarifies); filter for it via the
        // Unassigned quick-toggle or assignee:unassigned.
        valueGetter: (_value, row) => (row.assignee ? assigneeLabel(row.assignee) : "—"),
      },
      {
        field: "assignedAt",
        headerName: "Assigned",
        width: 130,
        valueGetter: (_value, row) =>
          row.assignedAt ? relativeLogTime(row.assignedAt, ALWAYS_RELATIVE_CUTOFF_HOURS) : "—",
      },
      {
        field: "assignedBy",
        headerName: "Assigned by",
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (_value, row) => (row.assignedBy ? assigneeLabel(row.assignedBy) : "—"),
      },
      {
        // Compact staff-comments affordance: the orange chat-bubble (with its
        // count) opens the same comment-thread popover used on the manifest
        // drawer + review dialogs. Hidden by default (see
        // initialColumnVisibilityModel). The count comes from the page query's
        // batched `commentCount` (one DataLoader query per page), so cells don't
        // each fire their own request.
        field: "comments",
        headerName: "Comments",
        width: 96,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <ManifestCommentsPopover
            processedManifestId={row.processedManifestId}
            count={row.commentCount}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: (params: GridRowParams<ManifestReviewRow>) =>
          params.row.assignee
            ? [
                <GridActionsCellItem
                  key="unassign"
                  icon={
                    <Tooltip title="Unassign">
                      <PersonRemove />
                    </Tooltip>
                  }
                  label="Unassign"
                  onClick={() => applyAssignment([params.row], null)}
                />,
              ]
            : [
                <GridActionsCellItem
                  key="assign"
                  icon={
                    <Tooltip title="Assign">
                      <PersonAdd />
                    </Tooltip>
                  }
                  label="Assign"
                  onClick={() => openAssignFor([params.row])}
                />,
              ],
      },
    ],
    [applyAssignment, openAssignFor, openQuickLink]
  );

  // Completed tab columns: manifest # (drawer link), tenant/facility identity, shipped +
  // completed dates, the contributor set, and a compact "what was done" summary. No
  // action/assignment columns — the work is done.
  const completedColumns: OmnisearchGridColDef<ManifestReviewRow>[] = useMemo(
    () => [
      manifestNumberColumn(openQuickLink),
      TENANT_COLUMN,
      FACILITY_COLUMN,
      SHIPPED_DATE_COLUMN,
      {
        field: "completedAt",
        headerName: "Completed Date",
        width: 150,
        valueFormatter: (value) => (value ? getUtcDisplayDate(value, "med") : "—"),
      },
      {
        field: "completedBy",
        headerName: "Completed By",
        flex: 1,
        minWidth: 150,
        sortable: false,
        // Distinct contributor set (resolvers + reviewers); "System" for auto-resolutions.
        valueGetter: (_value, row) =>
          row.completedBy && row.completedBy.length > 0 ? row.completedBy.join(", ") : "—",
      },
      {
        field: "completedSummary",
        headerName: "What Was Done",
        flex: 1.5,
        minWidth: 200,
        sortable: false,
        valueGetter: (_value, row) => row.completedSummary || "—",
      },
    ],
    [openQuickLink]
  );

  // Bulk assign/unassign live in the grid toolbar, to the right of Manage Columns
  // (the All/Mine/Unassigned quick filters now live in the summary scoreboard).
  // Unassign isn't a destructive action, so it stays the normal primary blue.
  const bulkActionButtons = [
    <Button
      key="assign"
      variant="contained"
      size="small"
      disabled={assigning || selectedRows.length === 0}
      onClick={() => openAssignFor(selectedRows)}
    >
      {selectedRows.length > 0 ? `Assign (${selectedRows.length})` : "Assign"}
    </Button>,
    <Tooltip key="unassign" title="Remove assignment">
      <span>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          disabled={assigning || selectedRows.length === 0}
          startIcon={<DeleteOutline />}
          onClick={async () => {
            await applyAssignment(selectedRows, null);
            setSelectedRows([]);
          }}
        >
          Unassign
        </Button>
      </span>
    </Tooltip>,
  ];

  // Completed tab filter list with the Completed-date picker floored to 18 months ago,
  // matching the backend's hard rolling-window boundary so the two can't disagree. Computed
  // per page mount so the floor tracks the current date.
  const completedFilterColumns = useMemo<FilterKey[]>(() => {
    const floor = new Date();
    floor.setUTCMonth(floor.getUTCMonth() - 18);
    const minDate = floor.toISOString().slice(0, 10);
    return MANIFEST_REVIEW_COMPLETED_FILTERS.map((f) =>
      f.key === "completedDate" ? { ...f, minDate } : f
    );
  }, []);

  // Export sits alongside the bulk actions and receives the grid's live omnisearch
  // string so it covers exactly the filtered set (all matching rows, not just the
  // visible page).
  const commandButtons = ({ search }: { search: string }) => [
    ...bulkActionButtons,
    <ExportDataButton
      key="export"
      exportType="manifestReview"
      fileName="manifest-review"
      filter={{ search: search || undefined }}
      // The queue mutates as staff assign/unassign without changing the filter, so
      // never serve a cached export — each click re-queries the current queue.
      disableCache
    />,
  ];

  return (
    <Stack spacing={3} sx={{ px: 3, py: 3 }}>
      {/* The scoreboard is an open-work triage tool: its counts and toggle tiles are all
          about open work, so it's shown only on the Open tab. On the Completed tab those
          tiles would be dead or misleading (the filters they write don't apply there), so
          the tab bar's count and the omnisearch filters stand on their own. */}
      {!isCompleted && (
        <ManifestReviewSummary
          onApplyFilter={applyOmnisearch}
          onToggle={toggleFilter}
          onClearAll={() => writeOmnisearch({})}
          activeTerms={activeTerms}
          completedActive={isCompleted}
          onSelectCompleted={() => setCompletionState("COMPLETED")}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Tabs
          value={completionState}
          onChange={(_e, next) => setCompletionState(next)}
          sx={{ mb: 2 }}
        >
          <Tab
            value="OPEN"
            label={!isCompleted && gridCount != null ? `Open (${gridCount})` : "Open"}
          />
          <Tab
            value="COMPLETED"
            label={isCompleted && gridCount != null ? `Completed (${gridCount})` : "Completed"}
          />
        </Tabs>
        <OmnisearchDataGrid<
          StaffManifestReviewManifestsQuery,
          StaffManifestReviewManifestsQueryVariables,
          ManifestReviewRow
        >
          // Remount on tab switch: the two tabs use different columns, filters, and default
          // sort, so a fresh grid avoids leaking one tab's column/sort state into the other.
          key={completionState}
          persistenceKey={
            isCompleted ? "manifest-review-completed-v1" : "manifest-review-manifests-v1"
          }
          isURLDriven
          // Paper owns the padding; suppress the omnisearch bar's own horizontal padding.
          withPadding={false}
          // Structured filters only (Tenant type-ahead, Status dropdown, Assignee
          // type-ahead); suppress the per-column free-text filters.
          additionalFilterColumns={isCompleted ? completedFilterColumns : MANIFEST_REVIEW_FILTERS}
          excludeFilterColumns={
            isCompleted
              ? MANIFEST_REVIEW_COMPLETED_EXCLUDED_FILTERS
              : MANIFEST_REVIEW_EXCLUDED_FILTERS
          }
          // Completed rows are finished, so no bulk assign/unassign toolbar there.
          commandButtons={isCompleted ? undefined : commandButtons}
          columns={isCompleted ? completedColumns : rawColumns}
          // Default view leads with Staff Actions Required; the detail columns
          // (sources, ship date, document review, the two discrepancy columns,
          // e-Manifest compliance) and "Delegated by" are hidden by default and
          // available via Manage Columns. The Completed tab shows all its (fewer) columns.
          initialColumnVisibilityModel={
            isCompleted
              ? undefined
              : {
                  sources: false,
                  shippedDate: false,
                  documentReview: false,
                  encampDiscrepancies: false,
                  epaCorrections: false,
                  eManifestCompliance: false,
                  assignedBy: false,
                  comments: false,
                }
          }
          dataQuery={STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY}
          dataQueryVariables={{ completionState }}
          getItems={(data) => data.staffManifestReviewManifests.items}
          getCount={(data) => data.staffManifestReviewManifests.totalCount}
          onDataStateChange={(data) => {
            setGridCount(data?.staffManifestReviewManifests.totalCount);
          }}
          // No selection on the Completed tab: it has no bulk actions, and OmnisearchDataGrid
          // force-enables checkboxes whenever a selection handler is passed, so the handler
          // itself (not just checkboxSelection) must be withheld to suppress the checkboxes.
          checkboxSelection={!isCompleted}
          disableRowSelectionOnClick
          selectedIds={isCompleted ? undefined : selectedIds}
          onSelectedRowsChanged={isCompleted ? undefined : setSelectedRows}
          onRowClick={(params) => setDetailRow(params.row)}
          initialPageSize={50}
          rowHeight="auto"
        />
      </Paper>

      <AssignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={handleAssign}
        title="Assign to Staff"
        selectedCount={assignTargets.length}
        isLoading={assigning}
      />

      {detailRow && (
        <ManifestDetailDialog
          open
          onClose={() => setDetailRow(null)}
          mode="staff"
          processedManifestId={detailRow.processedManifestId}
          manifestTrackingNumber={detailRow.manifestTrackingNumber}
          facilityName={detailRow.facilityName}
          staffDetails={{
            tenantName: detailRow.tenantName,
            facilityEpaId: detailRow.facilityEpaId,
            status: detailRow.status,
            blockedReason: detailRow.blockedReason,
            blockedNote: detailRow.blockedNote,
            assignee: detailRow.assignee,
            documentIds: detailRow.documentIds,
            onAssign: () => {
              openAssignFor([detailRow]);
              setDetailRow(null);
            },
          }}
        />
      )}

      {/* In-place quick links opened from a row (no navigation): the manifest drawer,
          the document-review modal, or the discrepancy dialog on a specific tab. The
          queue is cross-tenant, so we pass the row's tenant explicitly to the data
          queries AND wrap in TenantFeatureFlagsProvider so the flag-gated sections
          render against the row's tenant (not the staff user's session tenant). */}
      {quickLink && (
        <TenantFeatureFlagsProvider tenantId={quickLink.tenantId}>
          {quickLink.kind === "drawer" && (
            <ManifestDetails
              open
              processedManifestId={quickLink.processedManifestId}
              tenantId={quickLink.tenantId}
              onClose={() => setQuickLink(null)}
            />
          )}
          {quickLink.kind === "docReview" &&
            // Fast path: open straight from the row's needs-review document ids (the
            // dialog resolves the manifest from the doc). Only when there are none
            // (e.g. an already-reviewed manifest) fall back to resolving via the
            // heavier processed-manifest fetch.
            (quickLink.documentIds.length > 0 ? (
              <ManifestReviewDialog
                documentIds={quickLink.documentIds}
                onClose={() => setQuickLink(null)}
              />
            ) : (
              <DocumentReviewPermalink
                processedManifestId={quickLink.processedManifestId}
                tenantId={quickLink.tenantId}
                onClose={() => setQuickLink(null)}
              />
            ))}
          {quickLink.kind === "discrepancies" && (
            <DiscrepancyReviewDialog
              processedManifestIds={[quickLink.processedManifestId]}
              tenantId={quickLink.tenantId}
              initialSection={quickLink.tab}
              manifestTrackingNumber={quickLink.manifestTrackingNumber ?? undefined}
              onMutated={onDiscrepancyDialogMutated}
              onClose={onDiscrepancyDialogClose}
            />
          )}
        </TenantFeatureFlagsProvider>
      )}
    </Stack>
  );
}
